import { withZod } from '@remix-validated-form/with-zod'
import { addYears, subYears } from 'date-fns'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import type { ModelOrganization, ModelUser } from '~/models'
import { getHeightValue, regexEmail, regexPhone } from '~/models'
import {
  fmtAcres,
  fmtCurrencyAmount,
  fmtDate,
  fmtNumberAbbr,
  fmtPlumbingCapacity,
  fmtPower,
} from '~/utils'
import type {
  Listing,
  ListingLeaseSpace,
  ListingMeta,
  ListingTransaction,
  MetaFieldConfig,
  TransactionBroker,
} from './types'
import { getLotSizeFromMeta } from './lotSize'

export const META_FIELDS: { [key: string]: MetaFieldConfig } = {
  description: { zod: zfd.text(z.string().optional()), render: false },
  highlights: { zod: zfd.text(z.string().optional()), render: false },
  notes: { zod: zfd.text(z.string().optional()), render: false },

  // Sale
  priceNegotiable: {
    zod: zfd.checkbox().optional(),
    render: false,
  },
  price: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Sale Price',
    labelFormat: ({ status }) => {
      return status !== 'active' && status !== 'pending'
        ? 'Listed Price'
        : 'Sale Price'
    },
    format: ({ value, meta, valuesOnly }) => {
      if (meta.priceNegotiable) {
        return valuesOnly ? null : 'Negotiable'
      }
      if (valuesOnly) {
        return value
      }
      if (meta.priceMax) {
        if (meta.priceType === 'sf') {
          return `$${fmtCurrencyAmount(value)} - ${fmtCurrencyAmount(meta.priceMax)}/SF`
        } else if (meta.priceType === 'acre') {
          return `$${fmtCurrencyAmount(value)} - ${fmtCurrencyAmount(meta.priceMax)}/ac`
        }
        return `$${fmtCurrencyAmount(value)} - ${fmtCurrencyAmount(meta.priceMax)}`
      }
      if (meta.priceType === 'sf') {
        return `$${fmtCurrencyAmount(value)}/SF`
      } else if (meta.priceType === 'acre') {
        return `$${fmtCurrencyAmount(value)}/ac`
      }
      return `$${fmtCurrencyAmount(value)}`
    },
  },
  priceMax: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Sale Price Max',
    format: ({ value, valuesOnly }) => {
      if (valuesOnly) {
        return value
      }
    },
  },
  priceType: {
    zod: zfd.text(z.string().optional().default('total')),
    // render: false,
    format: ({ value, valuesOnly, meta }) => {
      if (valuesOnly && !meta.priceNegotiable) {
        return value
      }
    },
  },
  pricePerSf: {
    label: 'Price Per SF',
    generated: true,
    format: ({ meta, valuesOnly, propertyTypes }) => {
      const lotSize = getLotSizeFromMeta(meta)
      if (meta.priceNegotiable) {
        return null
      }
      if (
        propertyTypes &&
        propertyTypes.length > 0 &&
        propertyTypes[0] === 'Land' &&
        meta.price &&
        meta.priceType === 'total' &&
        lotSize.sf.min
      ) {
        const pricePerSf = meta.price / lotSize.sf.min
        return valuesOnly
          ? `${fmtNumberAbbr(pricePerSf)}`
          : `$${fmtNumberAbbr(pricePerSf)}/SF`
      } else if (valuesOnly) {
        return null
      } else if (meta.priceType !== 'total') {
        return null
      } else if (meta.price && meta.buildingSf) {
        const pricePerSf = meta.price / meta.buildingSf
        if (pricePerSf < 10) {
          return null
        }
        return valuesOnly
          ? `${fmtCurrencyAmount(pricePerSf)}`
          : `$${fmtCurrencyAmount(pricePerSf)}/SF`
      }
      return null
    },
  },
  pricePerAcre: {
    label: 'Price Per Acre',
    generated: true,
    format: ({ meta, propertyTypes }) => {
      const lotSize = getLotSizeFromMeta(meta)
      if (meta.priceNegotiable) {
        return null
      } else if (meta.priceType !== 'total') {
        return null
      } else if (
        propertyTypes[0] === 'Land' &&
        meta.price &&
        lotSize.unit !== 'sf' &&
        lotSize.acres.min
      ) {
        // TODO price range
        return `$${fmtNumberAbbr(meta.price / lotSize.acres.min)}/ac`
      }
      // if (meta.price && meta.acres && !meta.buildingSf) {
      //   return `$${fmtNumberAbbr(meta.price / meta.acres)}/ac`
      // }
      return null
    },
  },
  // investment: { zod: zfd.checkbox().optional() },
  capRate: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    suffix: '%',
  },
  buildingSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Building Size',
    suffix: 'SF',
  },
  // TODO Move down to industrial section after app refactor
  officeSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Office SF',
    suffix: 'SF',
  },
  warehouseSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Warehouse SF',
    suffix: 'SF',
  },
  acres: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Lot Size',
    format: ({ value, meta }) => {
      if (meta.lotSizeUnit === 'sf' && meta.lotSizeMin) {
        const min = Math.round(meta.lotSizeMin * 100) / 100
        const max =
          meta.lotSizeMax !== undefined
            ? Math.round(meta.lotSizeMax * 100) / 100
            : undefined

        if (max) {
          return `${min.toLocaleString()} - ${max.toLocaleString()} SF`
        }
        return `${min.toLocaleString()} SF`
      }

      const min = meta.lotSizeMin ?? value
      const max = meta.lotSizeMax ?? meta.acresMax
      if (max) {
        return `${fmtAcres(min)} - ${fmtAcres(max)} ac`
      }
      return `${fmtAcres(min)} ac`
    },
  },
  // acresMax: {
  //   zod: zfd.numeric(z.number().nonnegative().optional()),
  //   label: 'Lot Size Max',
  //   format: ({ value, valuesOnly }) => {
  //     if (valuesOnly) {
  //       return value
  //     }
  //   },
  // },
  lotSizeMin: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Lot Size',
    render: false,
  },
  lotSizeMax: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Lot Size Max',
    render: false,
  },
  lotSizeUnit: {
    zod: zfd.text(z.enum(['acres', 'sf']).optional()),
    label: 'Lot Size Unit',
    render: false,
  },
  buildToSuit: { zod: zfd.checkbox().optional() },
  isVacant: { zod: zfd.checkbox().optional() },
  zoning: { zod: zfd.text(z.string().optional()) },
  zoningDescription: {
    label: 'Zoning Desc.',
    zod: zfd.text(z.string().optional()),
  },

  // Investment
  grossIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  netOperatingIncome: {
    label: 'NOI',
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  proFormaNetOperatingIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Pro Forma NOI',
    format: 'currency',
  },
  totalExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  parkingIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  laundryIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  utilityIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  otherIncome: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  realEstateTaxes: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  personalPropertyTaxes: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  accountingExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  advertisingExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  insuranceExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  electricExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  waterSewerExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  trashExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  profManagerExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  residentManagerExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  maintenanceExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  suppliesExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  landscapingExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  reservesExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },
  otherExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
  },

  // Sale
  tenant: { zod: zfd.text(z.string().optional()) },
  formerTenant: { zod: zfd.text(z.string().optional()) },
  saleTerms: { zod: zfd.text(z.string().optional()), level: 'advanced' },
  upForAuction: { zod: zfd.checkbox().optional(), level: 'advanced' },
  agentOwnershipInterest: { zod: zfd.checkbox().optional() },
  auctionType: { zod: zfd.text(z.string().optional()), level: 'advanced' },
  auctionReserveAmount: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    format: 'currency',
  },
  auctionLicenseNumber: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  sealedBids: { zod: zfd.checkbox().optional(), level: 'advanced' },
  callForOffers: { zod: zfd.checkbox().optional(), level: 'advanced' },
  shortSale: { zod: zfd.checkbox().optional(), level: 'advanced' },

  // Lease
  structure: { zod: zfd.text(z.string().optional()), label: 'Lease Type' },
  expenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'currency',
    suffix: '/SF',
  },
  expensesType: { zod: zfd.text(z.string().optional()) },
  taxYearActual: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Tax Year (Actual)',
  },
  taxYearBase: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Tax Year (Base)',
  },
  signage: { zod: zfd.text(z.string().optional()) },
  doNotDisturbTenant: { zod: zfd.checkbox().optional(), level: 'advanced' },
  showingInstructions: {
    label: 'Showing',
    zod: zfd.text(z.string().optional()),
  },
  shortTermLease: { zod: zfd.checkbox().optional(), level: 'advanced' },
  // operatingExpenses: {
  //   zod: zfd.numeric(z.number().nonnegative().optional()),
  //   level: 'advanced',
  // },
  // camExpenses: {
  //   zod: zfd.numeric(z.number().nonnegative().optional()),
  //   level: 'advanced',
  // },
  rentEscalators: { zod: zfd.text(z.string().optional()), level: 'advanced' },
  rentConcessions: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    format: 'currency',
  },
  itAllowance: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    format: 'currency',
    label: 'IT Allowance',
  },
  movingAllowance: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    format: 'currency',
  },
  existingLeaseBuyoutAllowance: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    format: 'currency',
  },
  equityLease: { zod: zfd.checkbox().optional(), level: 'advanced' },

  // Building Details
  buildingName: {
    zod: zfd.text(z.string().optional()),
  },
  businessPark: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  buildingClass: {
    zod: zfd.text(z.string().optional()),
  },
  leedCertified: {
    zod: zfd.checkbox().optional(),
    label: 'LEED Certified',
  },
  energyStarCertified: {
    zod: zfd.checkbox().optional(),
  },
  minDivisible: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'number',
  },
  maxContiguous: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: 'number',
  },
  numberOfBuildings: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: '# of Buildings',
  },
  floors: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  yearBuilt: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  yearRenovated: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
  },
  roofType: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  constructionSiding: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  constructionType: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  parkingRatio: {
    zod: zfd.text(z.string().optional()),
    format: ({ value }) => {
      if (
        value.includes('/') ||
        value.includes('1000') ||
        value.includes('1,000')
      )
        return value
      return `${value} / 1,000`
    },
  },
  parkingType: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  parkingSpaces: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  passengerElevators: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
  },
  freightElevators: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
  },
  sprinklers: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  heatType: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  heatSource: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  airConditioning: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  internetAccess: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  rentableSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Rentable SF',
    format: 'number',
  },
  usableSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Usable SF',
    suffix: 'SF',
  },
  coreFactor: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
  },
  tenancy: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  typicalFloorSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    suffix: 'SF',
  },
  propertyCondition: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },

  submarket: {
    zod: zfd.text(z.string().optional()),
  },
  submarketType: {
    zod: zfd.text(z.string().optional()),
  },
  nearestMsa: {
    zod: zfd.text(z.string().optional()),
    label: 'Nearest MSA',
  },
  taxIdNumber: {
    zod: zfd.text(z.string().optional()),
    label: 'Tax ID Number',
  },
  legalDescription: {
    zod: zfd.text(z.string().optional()),
  },
  propertyUseType: {
    zod: zfd.text(z.string().optional()),
  },
  propertyStatus: {
    zod: zfd.text(z.string().optional()),
  },
  vehiclesPerDay: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    suffix: 'VPD',
    format: 'number',
  },
  averageOccupancyRate: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Avg. Occupancy',
    level: 'advanced',
    suffix: '%',
  },
  frontage: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Lot Frontage',
    suffix: 'ft',
    format: 'number',
  },
  lotSize: {
    label: 'Lot Size',
    generated: true,
    format: ({ meta }) => {
      if (meta.lotWidth && meta.lotDepth) {
        return `${fmtNumberAbbr(meta.lotWidth)}' x ${fmtNumberAbbr(meta.lotDepth)}'`
      } else if (meta.lotWidth) {
        return `${fmtNumberAbbr(meta.lotWidth)}' wide`
      } else if (meta.lotDepth) {
        return `${fmtNumberAbbr(meta.lotDepth)}' deep`
      }
      return null
    },
  },
  lotWidth: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Lot Size',
    suffix: 'ft',
    render: false,
  },
  lotDepth: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Lot Depth',
    suffix: 'ft',
    render: false,
  },
  inOpportunityZone: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
  },
  easements: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  plannedDevelopment: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
  },
  inFloodPlain: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
  },

  //Office

  //Retail
  hasDriveThru: { zod: zfd.checkbox().optional() },
  isEndCap: { zod: zfd.checkbox().optional() },

  //Multifamily
  totalUnits: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  totalBeds: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  laundry: { zod: zfd.text(z.string().optional()) },

  //Hospitality
  nameOfEstablishment: {
    zod: zfd.text(z.string().optional()),
    label: 'Name of Establishment',
  },

  // Business
  name: {
    zod: zfd.text(z.string().optional()),
    label: 'Business Name',
  },
  category: {
    zod: zfd.text(z.string().optional()),
    label: 'Business Category',
  },
  franchiseParentName: {
    zod: zfd.text(z.string().optional()),
  },
  franchisedBusiness: {
    zod: zfd.checkbox().optional(),
    label: 'Franchised Business',
  },
  yearEstablished: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Year Established',
  },
  fullTimeEmployees: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Full Time Employees',
  },
  partTimeEmployees: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Part Time Employees',
  },
  licenses: {
    zod: zfd.text(z.string().optional()),
    label: 'Licenses',
  },
  grossRevenue: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Gross Revenue',
    format: 'currency',
  },
  operatingExpenses: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Operating Expenses',
    format: 'currency',
  },
  cashFlow: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Cash Flow',
    format: 'currency',
  },
  valueFFAndE: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Value FF&E',
    format: 'currency',
  },
  valueInventory: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Value Inventory',
    format: 'currency',
  },
  realEstateStatus: {
    zod: zfd.text(z.string().optional()),
    label: 'Real Estate Status',
  },
  realEstateDescription: {
    zod: zfd.text(z.string().optional()),
    label: 'Real Estate Description',
  },
  // Industrial
  officeCeilingHeight: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value }) => getHeightValue(value),
  },
  dockNumber: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  dockSize: {
    zod: zfd.text(z.string().optional()),
  },
  driveInNumber: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Drive-In Number',
  },
  driveInSize: {
    zod: zfd.text(z.string().optional()),
    label: 'Drive-In Size',
  },
  trailerParkingSpaces: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  ceilingHeightMin: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Ceiling Height',
    format: ({ meta }) => {
      return [
        getHeightValue(meta.ceilingHeightMin),
        getHeightValue(meta.ceilingHeightMax),
      ]
        .filter((v) => v)
        .join(' - ')
    },
  },
  ceilingHeightMax: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    render: false,
  },
  clearHeightMin: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Clear Height',
    format: ({ meta }) => {
      return [
        getHeightValue(meta.clearHeightMin),
        getHeightValue(meta.clearHeightMax),
      ]
        .filter((v) => v)
        .join(' - ')
    },
  },
  clearHeightMax: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    render: false,
  },
  eaveHeight: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value }) => getHeightValue(value),
  },
  columnSpacing: {
    zod: zfd.text(z.string().optional()),
  },
  overheadCranes: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  }, // overheadCranes to craneNumber
  lighting: {
    zod: zfd.text(z.string().optional()),
  },
  railDoors: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
  },
  mezzanineSf: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
    label: 'Mezzanine SF',
    suffix: 'SF',
  },
  mezzanineHvac: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
    label: 'Mezzanine HVAC',
  },

  warehouseHvac: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
    label: 'Warehouse HVAC',
  },
  freightDockNumber: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    level: 'advanced',
  },
  freightDockSize: {
    zod: zfd.text(z.string().optional()),
    level: 'advanced',
  },
  yard: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
    format: ({ value, meta }) => {
      if (meta.yardFenced && meta.yardPaved) {
        return 'Fenced, Paved'
      } else if (meta.yardFenced) {
        return 'Fenced'
      } else if (meta.yardPaved) {
        return 'Paved'
      } else if (value === true) {
        return 'Yes'
      }
      return value
    },
  },
  yardFenced: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
    render: false,
  },
  yardPaved: {
    zod: zfd.checkbox().optional(),
    level: 'advanced',
    render: false,
  },

  //Land
  landSplitsAvailable: { zod: zfd.checkbox().optional() },
  adjacentParcelsAvailable: { zod: zfd.checkbox().optional() },
  wetlands: { zod: zfd.checkbox().optional() },
  waterfront: { zod: zfd.checkbox().optional() },
  siteClassification: {
    zod: zfd.text(z.string().optional()),
  },
  topography: { zod: zfd.text(z.string().optional()) },
  soilType: { zod: zfd.text(z.string().optional()) },

  //Transit
  nearestInterstate: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.distanceToInterstate
        ? `${value} - ${meta.distanceToInterstate} mi`
        : value
    },
  },
  distanceToInterstate: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value, meta }) => {
      return meta.nearestInterstate ? null : `${value} mi`
    },
  },
  nearestHighway: {
    label: 'Nearest Hwy',
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.distanceToHighway
        ? `${value} - ${meta.distanceToHighway} mi`
        : value
    },
  },
  distanceToHighway: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value, meta }) => {
      return meta.nearestHighway ? null : `${value} mi`
    },
  },
  nearestAirport: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.distanceToAirport
        ? `${value} - ${meta.distanceToAirport} mi`
        : value
    },
  },
  distanceToAirport: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value, meta }) => {
      return meta.nearestAirport ? null : `${value} mi`
    },
  },
  nearestPort: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.distanceToPort
        ? `${value} - ${meta.distanceToPort} mi`
        : value
    },
  },
  distanceToPort: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value, meta }) => {
      return meta.nearestPort ? null : `${value} mi`
    },
  },
  craneCapacityTons: {
    label: 'Crane Capacity',
    zod: zfd.numeric(z.number().nonnegative().optional()),
    format: ({ value, valuesOnly }) => {
      if (valuesOnly) {
        return value
      }

      const unit = value === 1 ? 'ton' : 'tons'
      return `${value.toLocaleString()} ${unit}`
    },
  },

  //Utilities
  power: { zod: zfd.checkbox().optional(), label: 'Power', render: false },
  powerProvider: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.powerProviderType
        ? `${meta.powerProviderType} - ${value}`
        : value
    },
  },
  powerProviderType: { zod: zfd.text(z.string().optional()), render: false },
  powerDisplay: {
    generated: true,
    label: 'Power',
    format: ({ meta }) => {
      return fmtPower(meta.powerVolts, meta.powerAmps, meta.powerPhase)
    },
  },
  powerAmps: {
    zod: zfd.text(z.string().optional()),
    suffix: 'A',
    render: false,
  },
  powerVolts: {
    zod: zfd.text(z.string().optional()),
    suffix: 'V',
    render: false,
  },
  powerPhase: { zod: zfd.text(z.string().optional()), render: false },
  water: { zod: zfd.checkbox().optional(), render: false },
  waterProvider: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.waterProviderType
        ? `${meta.waterProviderType} - ${value}`
        : value
    },
  },
  waterProviderType: { zod: zfd.text(z.string().optional()), render: false },
  waterLineSize: { zod: zfd.numeric(z.number().nonnegative().optional()) },
  waterCapacity: { zod: zfd.numeric(z.number().nonnegative().optional()) },
  sewer: {
    zod: zfd.checkbox().optional(),
    render: true,
    format: ({ value, meta }) => {
      if (value === false) return undefined

      return fmtPlumbingCapacity(meta.sewerLineSize, meta.sewerCapacity)
    },
  },
  sewerProvider: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.sewerProviderType
        ? `${meta.sewerProviderType} - ${value}`
        : value
    },
  },
  sewerProviderType: { zod: zfd.text(z.string().optional()), render: false },
  sewerLineSize: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    render: false,
  },
  sewerCapacity: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    render: false,
  },
  naturalGas: { zod: zfd.checkbox().optional(), render: false },
  naturalGasProvider: {
    label: 'Gas Provider',
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.naturalGasProviderType
        ? `${meta.naturalGasProviderType} - ${value}`
        : value
    },
  },
  naturalGasProviderType: {
    zod: zfd.text(z.string().optional()),
    render: false,
  },
  rail: { zod: zfd.checkbox().optional(), render: false },
  railProvider: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.railProviderType
        ? `${meta.railProviderType} - ${value}`
        : value
    },
  },
  railType: { zod: zfd.text(z.string().optional()) },
  railSpurOnSite: {
    label: 'Rail Spur on Site',
    zod: zfd.checkbox().optional(),
  },
  fiberOptic: { zod: zfd.checkbox().optional(), render: false },
  fiberOpticProvider: {
    zod: zfd.text(z.string().optional()),
    format: ({ value, meta }) => {
      return value && meta.fiberOpticProviderType
        ? `${meta.fiberOpticProviderType} - ${value}`
        : value
    },
  },
  fiberOpticProviderType: {
    zod: zfd.text(z.string().optional()),
    render: false,
  },

  // Appraiser
  apprTransactedDate: {
    zod: zfd.text(z.string().optional()),
    render: false,
    // label: 'Transacted Date',
    // format: (transactedDate) =>
    //   transactedDate ? transactedDate.split('T')[0] : undefined,
  },
  apprLeaseLength: {
    zod: zfd.numeric(z.number().nonnegative().optional()),
    label: 'Lease Length',
  },
  apprExpectedOccupancy: {
    zod: zfd.text(z.string().optional()),
    label: 'Expected Occupancy',
    format: ({ value }) => fmtDate(value),
  },
  apprComments: {
    zod: zfd.text(z.string().optional()),
    label: 'Comments',
    render: false,
  },
  apprBuyerTenantType: {
    zod: zfd.text(z.string().optional()),
    label: 'Buyer Tenant Type',
  },
  apprBuyerTenantName: {
    zod: zfd.text(z.string().optional()),
    label: 'Buyer Tenant Name',
  },
  apprSellerLandlordType: {
    zod: zfd.text(z.string().optional()),
    label: 'Seller Landlord Type',
  },
  apprSellerLandlordName: {
    zod: zfd.text(z.string().optional()),
    label: 'Seller Landlord Name',
  },
  apprListingBrokerName: {
    zod: zfd.text(z.string().optional()),
    label: 'Listing Broker Name',
  },
  apprListingBrokerEmail: {
    zod: zfd.text(z.string().optional()),
    label: 'Listing Broker Email',
  },
  apprListingBrokerPhone: {
    zod: zfd.text(z.string().optional()),
    label: 'Listing Broker Phone',
  },
  apprListingBrokerCompany: {
    zod: zfd.text(z.string().optional()),
    label: 'Listing Broker Company',
  },
  apprSellingBrokerName: {
    zod: zfd.text(z.string().optional()),
    label: 'Selling Broker Name',
  },
  apprSellingBrokerEmail: {
    zod: zfd.text(z.string().optional()),
    label: 'Selling Broker Email',
  },
  apprSellingBrokerPhone: {
    zod: zfd.text(z.string().optional()),
    label: 'Selling Broker Phone',
  },
  apprSellingBrokerCompany: {
    zod: zfd.text(z.string().optional()),
    label: 'Selling Broker Company',
  },

  virtualTour: {
    zod: zfd.text(
      z
        .string()
        .optional()
        .refine(
          (val) => {
            if (!val) return true
            return val.toLowerCase().includes('matterport')
          },
          {
            message:
              'Only Matterport tours are supported. Please reach out to support to include another type',
          }
        )
    ),
    label: 'Virtual Tour',
    render: false,
  },
}

const zodMetaObject: { [key: string]: z.ZodType } = {}

Object.keys(META_FIELDS).forEach((key) => {
  if (META_FIELDS[key].zod) {
    zodMetaObject[key] = META_FIELDS[key].zod!
  }
})

const ModelCompanyLogoSchema = z
  .object({
    logo: z
      .object({
        _id: zfd.text(z.string()),
        url: zfd.text(z.string()),
        thumbUrl: zfd.text(z.string()),
      })
      .optional(),
  })
  .optional()

export const ListingBrokerSchema = z.object({
  _id: zfd.text(z.string()),
  slug: zfd.text(z.string()),
  tid: zfd.text(z.string()),
  status: zfd.text(z.string().optional()).optional(),
  firstName: zfd.text(z.string()),
  middleName: zfd.text(z.string().optional().nullable()),
  lastName: zfd.text(z.string()),
  email: zfd.text(z.string()),
  company: z.object({
    _id: zfd.text(z.string()),
    name: zfd.text(z.string()),
    slug: zfd.text(z.string()),
    tid: zfd.text(z.string()),
    meta: ModelCompanyLogoSchema,
  }),
  meta: z
    .object({
      mobile: zfd.text(z.string().optional()),
      office: zfd.text(z.string().optional()),
      officeExtension: zfd.text(z.string().optional()),
      title: zfd.text(z.string().optional()),
      memberships: z.string().optional(),
      licenseNumber: z.string().optional(),
      licenseExpiration: z.string().optional(),
      photo: z
        .object({
          _id: zfd.text(z.string()),
          url: zfd.text(z.string()),
          thumbUrl: zfd.text(z.string()),
        })
        .optional(),
    })
    .optional(),
})

const TransactionBrokerSchema = z.object({
  _id: zfd.text(z.string()),
  tid: zfd.text(z.string()),
  slug: zfd.text(z.string()),
  firstName: zfd.text(z.string()),
  lastName: zfd.text(z.string()),
  email: zfd.text(z.string()),
  company: z.object({
    _id: zfd.text(z.string()),
    name: zfd.text(z.string()),
    slug: zfd.text(z.string()),
    tid: zfd.text(z.string()),
    meta: ModelCompanyLogoSchema,
  }),
}) satisfies z.ZodType<TransactionBroker>

const rateTypeSchema = z.union([
  z.literal('sfYear'),
  z.literal('month'),
  z.literal('year'),
  z.literal('sfMonth'),
  z.literal('acMonth'),
  z.literal('acYear'),
])

const optionalPositiveNumberTreatZeroAsUndefined = zfd
  .numeric(z.number().nonnegative().optional())
  .transform((value) => {
    return value === 0 ? undefined : value
  })

const CreateListingTransactionSchema = z
  .object({
    _id: zfd.text(z.string()),
    tid: zfd.text(z.string().optional()),
    created: zfd.text(z.string()),
    modified: zfd.text(z.string()),
    date: z.string().min(1, 'Please select a transaction date'),
    publicFields: zfd.repeatable(z.array(z.string()).optional()),
    confidential: zfd.checkbox(),
    propertyType: z.string().optional(), //.min(1, 'Please select a primary use'), // Temporarily remove required validation
    sale: z
      .object({
        price: zfd.numeric(z.number().gt(0).optional()),
        buildingSf: optionalPositiveNumberTreatZeroAsUndefined, // error text not working 'Please enter a building size'
        acres: zfd.numeric(z.number().gt(0).optional()),
        terms: zfd.text(z.string().optional()),
      })
      .optional(),
    lease: z
      .object({
        rate: zfd.numeric(z.number().nonnegative().optional()),
        totalLeaseValue: zfd.numeric(z.number().nonnegative().optional()),
        rateType: zfd.text(rateTypeSchema.optional()),
        structure: zfd.text(z.string().optional()),
        footage: zfd.numeric(z.number().gt(0).optional()),
        acres: zfd.numeric(z.number().gt(0).optional()),
        tenantName: zfd.text(z.string().optional()),
        startDate: zfd.text(z.string().optional()),
        termInMonths: zfd.numeric(z.number().nonnegative().optional()),
        space: z.object({
          _id: zfd.text(z.string()),
          name: zfd.text(z.string().optional()),
        }),
      })
      .optional(),
    notes: zfd.text(z.string().optional()),
    listingBrokers: zfd.repeatable(
      z.array(TransactionBrokerSchema).min(1, 'Add at least one listing broker')
    ),
    tenantRepBrokers: zfd
      // .repeatable(z.array(z.custom<TransactionBroker>()))
      .repeatable(z.array(TransactionBrokerSchema))
      .optional(),
    noTenantRep: zfd.checkbox().optional(),
    externalTenantRep: zfd.checkbox().optional(),
    externalBroker: z
      .object({
        firstName: z.string().min(1, 'First name is required'),
        lastName: z.string().min(1, 'Last name is required'),
        email: z
          .string()
          .min(1, 'Email is required')
          .refine((val) => regexEmail(val), {
            message: 'Please enter a valid email',
          }),
        phone: z
          .string()
          .min(1, 'Phone number is required')
          .refine((val) => regexPhone(val), {
            message: 'Please enter a valid phone number.',
          }),
      })
      .optional(),
  })
  .refine(
    (val) => {
      return val.sale && !val.sale.buildingSf && !val.sale.acres ? false : true
    },
    {
      message: 'Enter either Building Size or Acres',
      path: ['sale.buildingSf'],
    }
  )
  .refine(
    (val) => {
      return val.lease && !val.lease.footage && !val.lease.acres ? false : true
    },
    { message: 'Enter either Square Footage or Acres', path: ['lease.footage'] }
  )
  .refine(
    (val) => {
      return val.lease && val.lease.rate && !val.lease.rateType ? false : true
    },
    { message: 'Please enter a rate type', path: ['lease.rateType'] }
  )
  .refine(
    (val) => {
      return val.lease &&
        (val.lease.rate || val.lease.totalLeaseValue) &&
        !val.lease.termInMonths
        ? false
        : true
    },
    { message: 'Please enter a term in months', path: ['lease.termInMonths'] }
  )
  .refine(
    (val) => {
      return val.lease?.rate && !val.lease?.structure ? false : true
    },
    { message: 'Please enter a lease type', path: ['lease.structure'] }
  )
  .refine(
    (val) => {
      if (val.noTenantRep || val.externalTenantRep) {
        return true
      }
      return (val.tenantRepBrokers || []).length === 0 ? false : true
    },
    {
      message: 'Select at least one Tenant Rep Broker',
      path: ['tenantRepBrokers'],
    }
  ) satisfies z.ZodType<ListingTransaction>

const today = new Date()
const minDate = subYears(today, 100)
const maxDate = addYears(today, 100)

function isValidDate(
  value: Date | string | undefined,
  minDate: Date,
  maxDate: Date
): boolean {
  if (value === undefined) return true

  try {
    const date = value instanceof Date ? value : new Date(value)

    return date >= minDate && date <= maxDate
  } catch {
    return false
  }
}

export const PropertySchema = z.object({
  address: zfd.text(z.string().optional()),
  address2: zfd.text(z.string().optional()),
  city: zfd.text(z.string().optional()),
  stateAbbr: zfd.text(z.string().min(1, 'State is required')),
  postalCode: zfd.text(
    z
      .string()
      .min(5, 'Postal Code should be five digits')
      .max(5, 'Use five digit postal code only')
      .optional()
  ),
  county: zfd.text(z.string().min(1, 'County is required')),
  country: zfd.text(z.string()),
  msa: zfd.text(z.string().optional()),
  latitude: zfd.numeric(
    z
      .number()
      .gte(-90, 'Enter value between -90 and 90 inclusive')
      .lte(90, 'Enter value between -90 and 90 inclusive')
  ),
  longitude: zfd.numeric(
    z
      .number()
      .gte(-180, 'Enter value between -180 and 180 inclusive')
      .lte(180, 'Enter value between -180 and 180 inclusive')
  ),
  placeId: zfd.text(z.string().optional()),
  parcelApn: zfd.text(z.string().optional()),
  additionalParcelApns: zfd.repeatable(z.array(z.string()).optional()),
  fipsCode: zfd.text(z.string().optional()),
})

export const ListingTransactionSchema = z.object({
  _id: zfd.text(z.string()),
  tid: zfd.text(z.string().optional()),
  created: zfd.text(z.string()),
  modified: zfd.text(z.string()),
  date: z.string().min(1, 'Please select a transaction date'),
  publicFields: zfd.repeatable(z.array(z.string()).optional()),
  confidential: zfd.checkbox(),
  propertyType: z.string().optional(), //min(1, 'Please select a primary use').optional(),
  sale: z
    .object({
      price: zfd.numeric(z.number().gt(0).optional()),
      buildingSf: optionalPositiveNumberTreatZeroAsUndefined, // error text not working 'Please enter a building size'
      acres: zfd.numeric(z.number().gt(0).optional()),
      terms: zfd.text(z.string().optional()),
    })
    .optional(),
  lease: z
    .object({
      rate: zfd.numeric(z.number().nonnegative().optional()),
      totalLeaseValue: zfd.numeric(z.number().nonnegative().optional()),
      rateType: zfd.text(rateTypeSchema.optional()),
      structure: zfd.text(z.string().optional()),
      footage: zfd.numeric(z.number().gt(0).optional()),
      acres: zfd.numeric(z.number().gt(0).optional()),
      tenantName: zfd.text(z.string().optional()),
      startDate: zfd.text(z.string().optional()),
      termInMonths: zfd.numeric(z.number().nonnegative().optional()),
      space: z.object({
        _id: zfd.text(z.string()),
        name: zfd.text(z.string().optional()),
      }),
    })
    .optional(),
  notes: zfd.text(z.string().optional()),
  listingBrokers: zfd
    .repeatable(
      z.array(TransactionBrokerSchema).min(1, 'Add at least one listing broker')
    )
    .optional(),
  tenantRepBrokers: zfd
    // .repeatable(z.array(z.custom<TransactionBroker>()))
    .repeatable(z.array(TransactionBrokerSchema))
    .optional(),
  noTenantRep: zfd.checkbox().optional(),
  externalTenantRep: zfd.checkbox().optional(),
  externalBroker: z
    .object({
      firstName: z.string().min(1, 'First name is required'),
      lastName: z.string().min(1, 'Last name is required'),
      email: z
        .string()
        .min(1, 'Email is required')
        .refine((val) => regexEmail(val), {
          message: 'Please enter a valid email',
        }),
      phone: z
        .string()
        .min(1, 'Phone number is required')
        .refine((val) => regexPhone(val), {
          message: 'Please enter a valid phone number.',
        }),
    })
    .optional(),
}) satisfies z.ZodType<ListingTransaction>

export const leaseSpaceStatuses = [
  'draft',
  'available',
  'pending',
  'leased',
  'withdrawn',
] as const

export type LeaseSpaceStatus = (typeof leaseSpaceStatuses)[number]

export const leaseSpaceStatusSchema = z.enum(leaseSpaceStatuses)

export const ListingLeaseSpaceSchema = z
  .object({
    _id: zfd.text(z.string()),
    tid: zfd.text(z.string().optional()),
    bacId: zfd.text(z.string().optional()),
    publicBacId: zfd.text(z.string().optional()),
    created: zfd.text(z.string()),
    modified: zfd.text(z.string()),
    name: zfd.text(z.string().optional()),
    floor: zfd.text(z.string().optional()),
    minRate: zfd.numeric(z.number().nonnegative().optional()),
    maxRate: zfd.numeric(z.number().nonnegative().optional()),
    rateType: zfd.text(rateTypeSchema.optional()),
    rateNegotiable: zfd.checkbox().optional(),
    minAvailableSf: zfd.numeric(z.number().nonnegative().optional()),
    maxAvailableSf: zfd.numeric(z.number().nonnegative().optional()),
    acres: zfd.numeric(z.number().nonnegative().optional()),
    sublease: zfd.checkbox(),
    available: zfd.checkbox(),
    status: leaseSpaceStatusSchema.optional(),
    vacant: zfd.checkbox(),
    availableDate: zfd.text(z.string().optional()),
    tenantName: zfd.text(z.string().optional()),
    description: zfd.text(z.string().optional()),
    formerUse: zfd.text(z.string().optional()),
    structure: zfd.text(z.string().optional()),
    buildToSuit: zfd.checkbox().optional(),

    // Industrial
    officeSf: zfd.numeric(z.number().nonnegative().optional()),
    warehouseSf: zfd.numeric(z.number().nonnegative().optional()),
    clearHeightMin: zfd.numeric(z.number().nonnegative().optional()),
    clearHeightMax: zfd.numeric(z.number().nonnegative().optional()),
    dockNumber: zfd.numeric(z.number().nonnegative().optional()),
    dockSize: zfd.text(z.string().optional()),
    driveInNumber: zfd.numeric(z.number().nonnegative().optional()),
    driveInSize: zfd.text(z.string().optional()),
    files: z
      .array(
        z.object({
          _id: zfd.text(z.string()),
          bacId: zfd.text(z.string().optional()),
          url: zfd.text(z.string()),
          title: zfd.text(z.string().optional()),
          requireEmail: zfd.checkbox().optional(),
          order: zfd.numeric(z.number().optional()),
        })
      )
      .optional()
      .default([]),
    images: z
      .array(
        z.object({
          _id: zfd.text(z.string()),
          bacId: zfd.text(z.string().optional()),
          url: zfd.text(z.string()),
          thumbUrl: zfd.text(z.string()),
          order: zfd.numeric(z.number().optional()),
        })
      )
      .optional()
      .default([]),
    virtualTour: zfd.text(
      z
        .string()
        .optional()
        .refine(
          (val) => {
            if (!val) return true
            return val.toLowerCase().includes('matterport')
          },
          {
            message:
              'Only Matterport tours are supported. Please reach out to support to include another type',
          }
        )
    ),
  })
  .refine(
    (val) => {
      if (
        val.minAvailableSf &&
        val.maxAvailableSf &&
        val.minAvailableSf > val.maxAvailableSf
      ) {
        return false
      }
      return true
    },
    {
      message: 'Max Available SF must be less than Min Available SF',
      path: ['maxAvailableSf'],
    }
  )
  .refine(
    (val) => {
      if (val.minAvailableSf && !val.maxAvailableSf) {
        return false
      } else if (val.maxAvailableSf && !val.minAvailableSf) {
        return false
      }
      return true
    },
    {
      message: 'Min and Max values must be provided',
      path: ['maxAvailableSf'],
    }
  )
  .refine(
    (val) => {
      if (val.minRate && val.maxRate && val.minRate > val.maxRate) {
        return false
      }
      return true
    },
    {
      message: 'Max Rate must be less than Min Rate',
      path: ['maxRate'],
    }
  )
  .refine(
    (val) => {
      if (val.minRate && !val.maxRate) {
        return false
      } else if (val.maxRate && !val.minRate) {
        return false
      }
      return true
    },
    {
      message:
        // 'Please set both a minimum and maximum rate (if fixed rate enter the value in both fields)',
        'Min and Max values must be provided',
      path: ['maxRate'],
    }
  ) satisfies z.ZodType<ListingLeaseSpace>

const LocationVisibilitySchema = z.union([
  z.literal('city'),
  z.literal('county'),
  z.literal('state'),
])

export const watermarkStatuses = [
  'pending-review',
  'watermarked',
  'not-watermarked',
] as const

export type WatermarkStatus = (typeof watermarkStatuses)[number]

export const watermarkStatusSchema = z.enum(watermarkStatuses)

export const ListingSchema = z
  .object({
    _id: z.string(),
    tid: z.string(),
    bacId: z.string().optional(),
    publicBacId: z.string().optional(),
    created: z.string(),
    modified: z.string(),
    coreModified: z.string().optional(),
    organization: z.custom<ModelOrganization>(),
    externalMembershipSlugs: z.array(z.string()).optional().default([]),
    user: z.custom<ModelUser>(),
    slug: zfd.text(z.string()),
    name: zfd.text(z.string().min(1)),
    status: zfd.text(z.string()),
    type: zfd.text(z.string()),
    tags: zfd.text(z.string().optional()),
    propertyTypes: zfd.repeatable(
      z
        .array(z.string())
        .min(1, 'Add at least one Primary Use')
        .max(2, 'Maximum of two Primary Uses')
    ),
    propertySubtypes: zfd.repeatable(z.array(z.string()).optional()),
    isPublic: zfd.checkbox().optional(), // Not actually used in the validator right now, set in server/listing
    company: z.object({
      _id: zfd.text(z.string()),
      name: zfd.text(z.string()),
      slug: zfd.text(z.string()),
      tid: zfd.text(z.string()),
      childCompanyIds: zfd.repeatable(z.array(z.string())).optional(),
      meta: ModelCompanyLogoSchema,
    }),
    availableDate: z.string().optional(),
    expirationDate: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true
        return isValidDate(value, minDate, maxDate)
      }, 'Date outside allowed range'),
    offMarketDate: z.string().optional(),
    activeDate: z.string().optional(),
    listDate: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true
        return isValidDate(value, minDate, maxDate)
      }, 'Date outside allowed range'),
    deletedDate: z.string().optional(),
    brokers: zfd.repeatable(z.array(ListingBrokerSchema)),
    location: z.object({
      type: z.string(),
      coordinates: z.array(zfd.numeric(z.number())),
    }),
    property: PropertySchema,
    disabledIntegrations: z.array(z.string()).optional(),
    isBusinessListing: zfd.checkbox().optional(),
    locationVisibility: LocationVisibilitySchema.optional(),
    options: z
      .object({
        leaseSpaceSorting: zfd.text(z.enum(['created', 'numeric']).optional()),
        hideParcelApn: zfd.checkbox().optional(),
        hideParcelOutline: zfd.checkbox().optional(),
        hideTotalAvailableSf: zfd.checkbox().optional(),
      })
      .optional(),
    leaseSpaces: z
      .array(ListingLeaseSpaceSchema)
      .optional()
      .refine(
        (val) => {
          if (val && val.length > 1) {
            const names = val.filter((v) => !v.name)
            // return names.length === 0
          }
          return true
        },
        { message: 'Define a Name for each space if more than one space' }
      ),
    meta: z
      .object(zodMetaObject)
      .refine(
        (val) => {
          if (
            val.ceilingHeightMin &&
            val.ceilingHeightMax &&
            val.ceilingHeightMin > val.ceilingHeightMax
          ) {
            return false
          }
          return true
        },
        {
          message: 'Ceiling Height minimum value must be less than maximum.',
          path: ['ceilingHeightMin'],
        }
      )
      .refine(
        (val) => {
          if (
            val.clearHeightMin &&
            val.clearHeightMax &&
            val.clearHeightMin > val.clearHeightMax
          ) {
            return false
          }
          return true
        },
        {
          message: 'Clear Height minimum value must be less than maximum.',
          path: ['clearHeightMin'],
        }
      )
      .refine(
        (meta: ListingMeta) => {
          if (meta.priceMax && !meta.price) {
            return false
          }
          return true
        },
        { message: 'Required with max', path: ['price'] }
      )
      .refine(
        (meta: ListingMeta) => {
          if (meta.acresMax && !getLotSizeFromMeta(meta).acres.min) {
            return false
          }
          return true
        },
        { message: 'Required with max', path: ['lotSizeMin'] }
      )
      .refine(
        (meta: ListingMeta) => {
          if (meta.lotSizeMax && !meta.lotSizeMin) {
            return false
          }
          return true
        },
        { message: 'Required with max', path: ['lotSizeMin'] }
      )
      .refine(
        (meta: ListingMeta) => {
          const lotSize = getLotSizeFromMeta(meta)
          const hasLotSize = !!lotSize.sf.min
          if (
            meta.priceType === 'sf' &&
            meta.price &&
            !meta.buildingSf &&
            !hasLotSize
          ) {
            return false
          }
          return true
        },
        {
          message:
            'Building SF or Lot Size is required when setting Price per SF',
          path: ['buildingSf'],
        }
      )
      .refine(
        (meta: ListingMeta) => {
          const lotSize = getLotSizeFromMeta(meta)
          if (meta.priceType === 'acre' && meta.price && !lotSize.acres.min) {
            return false
          }
          return true
        },
        {
          message: 'Lot Size is required when setting Price per Acre',
          path: ['lotSizeMin'],
        }
      ),

    links: z
      .array(
        z.object({
          name: zfd.text(z.string()),
          url: zfd.text(z.string()),
          type: zfd.text(z.string().optional()),
        })
      )
      .optional()
      .default([]),
    images: z
      .array(
        z.object({
          _id: zfd.text(z.string()),
          bacId: zfd.text(z.string().optional()),
          url: zfd.text(z.string()),
          thumbUrl: zfd.text(z.string()),
          isWatermarked: watermarkStatusSchema.optional(),
          order: zfd.numeric(z.number().optional()),
        })
      )
      .optional()
      .default([]),
    files: z
      .array(
        z.object({
          _id: zfd.text(z.string()),
          bacId: zfd.text(z.string().optional()),
          url: zfd.text(z.string()),
          title: zfd.text(z.string().optional()),
          requireEmail: zfd.checkbox().optional(),
          order: zfd.numeric(z.number().optional()),
        })
      )
      .optional()
      .default([]),

    streetView: z
      .object({
        pano: zfd.text(z.string()),
        heading: zfd.numeric(z.number()),
        pitch: zfd.numeric(z.number()),
        fov: zfd.numeric(z.number()),
        url: zfd.numeric(z.string().optional()),
      })
      .optional(),

    transactions: z.array(ListingTransactionSchema).optional().default([]),
  })
  .refine(
    (val) => {
      return !val.name.includes('COPY')
    },
    {
      message: 'Please update name after duplication',
      path: ['name'],
    }
  )
  .refine(
    (val) => {
      return true
      // if (val.type !== 'sale' && val.meta.structure) {
      //   return true
      // } else {
      //   return false
      // }
    },
    { message: 'Please specify the lease type', path: ['meta.structure'] }
  )
  .refine(
    (val) => {
      const isLand = val.propertyTypes?.[0] === 'Land'
      if (
        !isLand &&
        val.meta.priceType === 'sf' &&
        val.meta.price &&
        !val.meta.buildingSf
      ) {
        return false
      }
      return true
    },
    {
      message: 'Building SF is required when setting Price per SF',
      path: ['meta.buildingSf'],
    }
  )
  .refine(
    (val) => {
      const isLand = val.propertyTypes?.[0] === 'Land'
      const lotSize = getLotSizeFromMeta(val.meta)
      if (
        isLand &&
        val.meta.priceType === 'sf' &&
        val.meta.price &&
        !lotSize.sf.min
      ) {
        return false
      }
      return true
    },
    {
      message: 'Lot Size is required when setting Land Price per SF',
      path: ['meta.lotSizeMin'],
    }
  ) satisfies z.ZodType<Omit<Listing, 'filters'>>

export const listingBrokerValidator = withZod(ListingBrokerSchema)
export const createListingTransactionValidator = withZod(
  CreateListingTransactionSchema
)
